import React, { useState } from 'react';
import { Create } from '@refinedev/antd';
import { Form, Input, DatePicker, Button, Upload, message } from 'antd';
import { useTranslation } from '@refinedev/core';
import { BookOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const CreateClass = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();

  const [uploadId, setUploadId] = useState<string | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const { usePost } = useAPI();
  const { mutate: uploadImage, isLoading: uploadImageLoading } = usePost<
    { id: string },
    FormData
  >(endPoints.upload(), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const { mutate: createClass, isLoading: createClassLoading } = usePost(
    endPoints.class()
  );
  const handleChange = ({
    fileList: newFileList,
  }: {
    fileList: UploadFile[];
  }) => {
    setFileList(newFileList);
  };

  const handleUpload = () => {
    if (fileList.length > 0 && fileList[0].originFileObj) {
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      uploadImage(formData, {
        onSuccess: (response) => {
          setUploadId(response.data.data.id);
        },
      });
    }
  };
  const onSubmit = (values: {
    name: string;
    date: dayjs.Dayjs;
    description: string;
  }) => {
    createClass(
      {
        name: values.name,
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        banner_id: Number(uploadId),
      },
      {
        onSuccess: () => {
          form.resetFields();
          setUploadId(null);
          setFileList([]);
          message.success(translate('create-class.success'));
          navigate('/classes');
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  return (
    <Create
      title={
        <div className="flex items-center gap-3">
          <BookOutlined />
          <h1 className="text-2xl font-bold">
            {translate('create-class.title')}
          </h1>
        </div>
      }
      footerButtons={
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={createClassLoading}
          disabled={uploadId === null}
        >
          {translate('create-class.save')}
        </Button>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          label={translate('listing-classes.name')}
          name="name"
          rules={[
            {
              required: true,
              message: translate('create-class.validation.name'),
            },
          ]}
        >
          <Input placeholder={translate('listing-classes.name')} />
        </Form.Item>

        <Form.Item
          label={translate('listing-classes.date')}
          name="date"
          rules={[
            {
              required: true,
              message: translate('create-class.validation.date'),
            },
          ]}
          className="w-full"
        >
          <DatePicker
            className="w-full"
            placeholder={translate('listing-classes.date')}
          />
        </Form.Item>

        <Form.Item
          label={translate('listing-classes.description')}
          name="description"
          rules={[
            {
              required: true,
              message: translate('create-class.validation.description'),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={translate('listing-classes.description')}
          />
        </Form.Item>

        <Form.Item label={translate('listing-classes.banner')} name="banner">
          <div className="flex flex-wrap items-center gap-2">
            <Upload
              listType="picture"
              maxCount={1}
              fileList={fileList}
              disabled={uploadId !== null}
              onChange={handleChange}
              className="w-full"
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />} disabled={uploadId !== null}>
                {uploadId
                  ? translate('create-class.upload-success')
                  : translate('create-class.select-file')}
              </Button>
            </Upload>
            {!uploadId && (
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploadImageLoading}
                disabled={fileList.length === 0}
              >
                {translate('create-class.upload')}
              </Button>
            )}
          </div>
        </Form.Item>
      </Form>
    </Create>
  );
};
